// src/pages/wallet/WithdrawPage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiDownload, FiDollarSign, FiCreditCard, FiAlertCircle, FiCheckCircle, FiPhone } from "react-icons/fi";
import TextInput from "../../components/common/TextInput";
import Button from "../../components/common/Button";
import { withdrawMoney, getWalletOverview, getWithdrawLimit } from "../../api/walletApi";
import { useAuth } from "../../hooks/useAuth";
import {
  calculateFee,
  canTransferWithdrawAmount,
  STATUS_LEVEL,
} from "../../utils/status";
import { getProfile } from "../../api/userApi";

function WithdrawPage() {
  const { user } = useAuth();
  const userId = user?.user_id;

  const [form, setForm] = useState({
    amount: "",
    method: "bkash",
    number: "",
  });

  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [withdrawLimit, setWithdrawLimit] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [amountMessage, setAmountMessage] = useState("");

  useEffect(() => {
    if (!userId) {
      setError("User not found.");
      setInitialLoading(false);
      return;
    }

    async function loadData() {
      try {
        setInitialLoading(true);
        setError("");

        const [profileRes, walletRes] = await Promise.all([
          getProfile(userId),
          getWalletOverview(userId),
        ]);

        setProfile(profileRes);
        setWallet(walletRes.wallet || walletRes);
      } catch (err) {
        console.error(err);
        setError("Failed to load profile or wallet.");
      } finally {
        setInitialLoading(false);
      }
    }

    async function loadLimit() {
      try {
        const data = await getWithdrawLimit(userId);
        if (data.success) {
          setWithdrawLimit(data);
        }
      } catch (err) {
        console.error("Failed to load withdraw limit:", err);
      }
    }

    loadLimit();
    loadData();
  }, [userId]);

  const status = profile?.status || STATUS_LEVEL.NEW;
  const currentBalance = Number(wallet?.balance) || 0;

  const amountNumber = Number(form.amount) || 0;
  const isAmountEntered = form.amount !== "";

  const fee = amountNumber > 0 ? Number(calculateFee(amountNumber, status)) : 0;
  const totalDebit = amountNumber + fee;
  const balanceAfter = currentBalance - totalDebit;

  const canWithdraw = canTransferWithdrawAmount(
    amountNumber,
    currentBalance,
    status
  );

  const usedThisMonth = withdrawLimit?.used ?? 0;
  const monthlyLimit = withdrawLimit?.limit ?? 2;
  const remainingThisMonth =
    withdrawLimit?.remaining ?? Math.max(0, monthlyLimit - usedThisMonth);
  const hasReachedMonthlyLimit = remainingThisMonth <= 0;

  const nf = (v) => Number(v).toFixed(2);

  useEffect(() => {
    if (!isAmountEntered || !wallet) {
      setAmountMessage("");
      return;
    }

    if (amountNumber <= 0) {
      setAmountMessage("Amount must be greater than 0.");
      return;
    }

    if (status < STATUS_LEVEL.BRONZE) {
      setAmountMessage(
        "You can withdraw money only from Bronze account or higher."
      );
      return;
    }

    if (!canWithdraw) {
      setAmountMessage(
        `Invalid amount. You must withdraw more than 5€ and keep at least 5€ in your wallet after fee. Current balance: €${nf(
          currentBalance
        )}, fee: €${nf(fee)}, balance after: €${nf(balanceAfter)}.`
      );
      return;
    }

    setAmountMessage(
      `Fee: €${nf(fee)}. Balance after withdraw: €${nf(balanceAfter)}.`
    );
  }, [
    isAmountEntered,
    amountNumber,
    wallet,
    status,
    canWithdraw,
    currentBalance,
    fee,
    balanceAfter,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!profile || !wallet) {
      setError("Profile or wallet not loaded yet.");
      return;
    }

    if (status < STATUS_LEVEL.BRONZE) {
      setError("You can withdraw money only from Bronze account or higher.");
      return;
    }

    if (!amountNumber || amountNumber <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }

    if (!canWithdraw) {
      setError(
        "Invalid amount. You must withdraw more than 5€ and keep at least 5€ in your wallet after fee."
      );
      return;
    }

    if (!form.number) {
      setError("Withdraw number is required.");
      return;
    }

    if (hasReachedMonthlyLimit) {
      setError(
        "You already reached your monthly withdraw limit (2). You can withdraw again next month."
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        amount: amountNumber,
        method: form.method,
        number: form.number,
      };

      const data = await withdrawMoney(payload, userId);

      setSuccess(data.message || "Withdraw request submitted successfully.");
      setForm({
        amount: "",
        method: "bkash",
        number: "",
      });

      try {
        const updatedWallet = await getWalletOverview(userId);
        setWallet(updatedWallet.wallet || updatedWallet);
      } catch (err) {
        console.error("Failed to refresh wallet:", err);
      }
    } catch (err) {
      console.error(err);
      setError("Withdraw request failed. Please check your balance and information.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <div>Loading withdraw data...</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="mb-3 animate-fadeIn">
        <Link
          to="/wallet"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <FiArrowLeft className="mr-2 h-4 w-4" />
          Back to Wallet
        </Link>
      </div>

      <div className="animate-fadeIn">
        <h1 className="text-3xl font-bold text-gray-900">
          Withdraw Money
        </h1>
        <p className="text-gray-600 mt-1">
          Create a withdraw request to your bkash or roket account.
        </p>
      </div>

      {status < STATUS_LEVEL.BRONZE && (
        <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 animate-slideUp">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 mb-1">Withdraw Locked</p>
              <p className="text-amber-700">
                Your current account status is{" "}
                <span className="font-medium">{profile?.status || "Unknown"}</span>.{" "}
                You can transfer or withdraw money only when your account is{" "}
                <span className="font-semibold">Bronze</span> or higher (Silver / Gold).
              </p>
            </div>
          </div>
        </div>
      )}

      {wallet && (
        <div className="card bg-gradient-to-br from-primary-50 to-secondary-50 border-primary-200 animate-slideUp">
          <div className="flex items-start gap-3">
            <FiDollarSign className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-gray-900 mb-1">
                Current balance: <span className="text-primary-600">€{nf(currentBalance)}</span>
              </p>
              <p className="text-gray-600">
                Remember: you must withdraw more than 5€ and keep at least 5€ in your wallet after fee.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="card bg-gradient-to-br from-red-50 to-rose-50 border-red-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="card bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-700">{success}</p>
          </div>
        </div>
      )}

      <form className="card-gradient animate-scaleIn space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiDollarSign className="inline-block mr-2 h-4 w-4" />
              Amount (€)
            </label>
            <TextInput
              name="amount"
              type="number"
              value={form.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              min="0"
              step="0.01"
            />
            {amountMessage && (
              <div className={`flex items-start gap-2 text-xs mt-2 p-3 rounded-lg ${
                canWithdraw
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {canWithdraw ? (
                  <FiCheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                ) : (
                  <FiAlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                )}
                <p>{amountMessage}</p>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCreditCard className="inline-block mr-2 h-4 w-4" />
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, method: "bkash" }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  form.method === "bkash"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="text-center">
                  <div className="font-semibold text-gray-900">bKash</div>
                  <div className="text-xs text-gray-500 mt-1">Mobile Wallet</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, method: "roket" }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  form.method === "roket"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="text-center">
                  <div className="font-semibold text-gray-900">Rocket</div>
                  <div className="text-xs text-gray-500 mt-1">Mobile Wallet</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, method: "nagad" }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  form.method === "nagad"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="text-center">
                  <div className="font-semibold text-gray-900">Nagad</div>
                  <div className="text-xs text-gray-500 mt-1">Mobile Wallet</div>
                </div>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiPhone className="inline-block mr-2 h-4 w-4" />
              {form.method === "bkash" ? "bKash" : (form.method == "roket"? "Roket": "Nagad")} Personal Number
            </label>
            <TextInput
              name="number"
              value={form.number}
              onChange={handleChange}
              placeholder="Enter your mobile number"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={loading}
          disabled={loading || status < STATUS_LEVEL.BRONZE || !wallet || hasReachedMonthlyLimit}
          variant="primary"
        >
          <FiDownload className="inline-block mr-2 h-4 w-4" />
          Submit Withdraw Request
        </Button>
      </form>
    </div>
  );
}

export default WithdrawPage;
